import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../lib/api.js";

/**
 * Export history — docs/01-PRODUCT-SPEC.md §9.3.
 *
 * The download link is fetched per click rather than held in this list: it is a signed, single-use
 * token, so one minted when the page rendered would be spent (or stale) by the time anyone pressed
 * it. Asking for it at the moment of the click is both simpler and what makes single-use workable.
 */

interface ExportJob {
  id: string;
  formId: string | null;
  format: string;
  status: string;
  rowCount: number | null;
  error: string | null;
  googleSheetUrl: string | null;
  redactPii: boolean;
  createdAt: string;
  completedAt: string | null;
  expiresAt: string | null;
}

const STATUS_STYLES: Record<string, string> = {
  QUEUED: "bg-muted text-muted-foreground",
  RUNNING: "bg-accent/20 text-primary",
  COMPLETED: "bg-accent/20 text-primary",
  FAILED: "bg-brand-red/10 text-brand-red",
  EXPIRED: "bg-muted text-muted-foreground",
};

const lagos = (value: string): string =>
  new Date(value).toLocaleString("en-NG", { timeZone: "Africa/Lagos" });

export const ExportHistory = (): JSX.Element => {
  const client = useQueryClient();

  const jobs = useQuery({
    queryKey: ["exports"],
    queryFn: () => apiGet<{ data: ExportJob[] }>("/exports"),
    // A queued export finishes without anything on this page causing it to. Polling is what makes
    // the row change to "ready" while the user is still looking at it.
    refetchInterval: (query) =>
      (query.state.data?.data ?? []).some(
        (job) => job.status === "QUEUED" || job.status === "RUNNING",
      )
        ? 5_000
        : false,
  });

  const cancel = useMutation({
    mutationFn: (id: string) => apiPost(`/exports/${id}/cancel`),
    onSuccess: () => client.invalidateQueries({ queryKey: ["exports"] }),
  });

  const download = async (id: string): Promise<void> => {
    const detail = await apiGet<{ downloadUrl: string | null }>(`/exports/${id}`);
    if (!detail.downloadUrl) {
      await client.invalidateQueries({ queryKey: ["exports"] });
      return;
    }
    window.location.href = detail.downloadUrl;
  };

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">Exports</h2>

      {jobs.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {jobs.data?.data.length === 0 && (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          Nothing exported yet.
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-2">Requested</th>
              <th>Format</th>
              <th>Rows</th>
              <th>Personal data</th>
              <th>Status</th>
              <th>Available until</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(jobs.data?.data ?? []).map((job) => (
              <tr key={job.id} className="border-b">
                <td className="py-2">{lagos(job.createdAt)}</td>
                <td className="uppercase">{job.format}</td>
                <td>{job.rowCount?.toLocaleString() ?? "—"}</td>
                <td>{job.redactPii ? "Redacted" : "Included"}</td>
                <td>
                  <span
                    className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLES[job.status] ?? ""}`}
                  >
                    {job.status}
                  </span>
                  {job.error && (
                    <span className="ml-2 text-xs text-muted-foreground">{job.error}</span>
                  )}
                </td>
                <td>{job.expiresAt ? lagos(job.expiresAt) : "—"}</td>
                <td className="text-right">
                  {job.status === "COMPLETED" && job.googleSheetUrl && (
                    <a
                      href={job.googleSheetUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      Open sheet
                    </a>
                  )}
                  {job.status === "COMPLETED" && !job.googleSheetUrl && (
                    <button
                      type="button"
                      onClick={() => void download(job.id)}
                      className="rounded-md border px-2 py-1 hover:bg-muted"
                    >
                      Download
                    </button>
                  )}
                  {(job.status === "QUEUED" || job.status === "RUNNING") && (
                    <button
                      type="button"
                      onClick={() => cancel.mutate(job.id)}
                      className="rounded-md border px-2 py-1 hover:bg-muted"
                    >
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
